import React, { useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
} from "@mui/material";
import {
  Chart,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Legend,
  Tooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { analyzeClients } from "./analyze-clients";

Chart.register(CategoryScale, LinearScale, BarElement, Title, Legend, Tooltip);

function KpiCard({ value, label, color }) {
  return (
    <Card sx={{ flex: 1, textAlign: "center" }}>
      <CardContent sx={{ py: 2 }}>
        <Typography variant="h4" fontWeight={700} color={color || "primary"}>
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </CardContent>
    </Card>
  );
}

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: "top" },
    tooltip: { mode: "index" },
  },
  scales: {
    y: {
      beginAtZero: true,
      stacked: true,
      ticks: { stepSize: 1, precision: 0 },
      title: { display: true, text: "Nombre de clients" },
    },
    x: {
      stacked: true,
      title: {
        display: true,
        text: "Mois entre premier contact et installation",
      },
    },
  },
};

const installChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: "top" },
    tooltip: { mode: "index" },
  },
  scales: {
    y: {
      beginAtZero: true,
      stacked: true,
      ticks: { stepSize: 1, precision: 0 },
      title: { display: true, text: "Installations" },
    },
    x: {
      stacked: true,
      ticks: { maxRotation: 45 },
    },
  },
};

export default function ClientsTab({ clientsRows }) {
  const analysis = useMemo(() => {
    if (!clientsRows || clientsRows.length === 0) return null;
    return analyzeClients(clientsRows);
  }, [clientsRows]);

  if (!analysis) {
    return (
      <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
        Aucune donnée clients disponible.
      </Typography>
    );
  }

  return (
    <Box>

      {/* === Installations by month === */}
      <Typography variant="h6" sx={{ mt: 4, mb: 1.5 }}>
        Installations par mois (juil. 2023+) — Particulier vs Pro
      </Typography>

      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
        <KpiCard value={analysis.installations.total} label="Installations" />
      </Stack>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box>
            <Bar
              height={400}
              data={{
                labels: analysis.installations.labels,
                datasets: analysis.installations.datasets,
              }}
              options={installChartOptions}
            />
          </Box>
        </CardContent>
      </Card>

      <Typography variant="h6" sx={{ mb: 1.5 }}>
        Temps de conversion (juil. 2023+) — premier contact → installation
      </Typography>

      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
        <KpiCard value={analysis.kpis.total} label="Clients avec conversion" />
        <KpiCard
          value={`${analysis.kpis.median} mois`}
          label="Médiane"
          color="success.main"
        />
        <KpiCard value={`${analysis.kpis.mean} mois`} label="Moyenne" />
      </Stack>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box>
            <Bar
              height={400}
              data={{ labels: analysis.labels, datasets: analysis.datasets }}
              options={chartOptions}
            />
          </Box>
          {analysis.skipParts.length > 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1.5, fontStyle: "italic" }}
            >
              {analysis.skippedTotal} clients exclus sur{" "}
              {analysis.totalRows} ({analysis.skipParts.join(", ")}).
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* === By Type === */}
      <Typography variant="h6" sx={{ mt: 4, mb: 1.5 }}>
        Par type — Particulier vs Pro
      </Typography>

      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
        <KpiCard
          value={analysis.byType.kpis.Particulier.total}
          label={`Particuliers — méd. ${analysis.byType.kpis.Particulier.median} mois, moy. ${analysis.byType.kpis.Particulier.mean} mois`}
        />
        <KpiCard
          value={analysis.byType.kpis.Pro.total}
          label={`Pros — méd. ${analysis.byType.kpis.Pro.median} mois, moy. ${analysis.byType.kpis.Pro.mean} mois`}
          color="warning.main"
        />
      </Stack>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box>
            <Bar
              height={400}
              data={{
                labels: analysis.byType.labels,
                datasets: analysis.byType.datasets,
              }}
              options={chartOptions}
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
